/**
 * UserflowsViewer (Iter 9.39a)
 *
 * Tab in the admin Product sidebar that renders /app/memory/MAIN_USER_FLOWS.md
 * inside the existing AdminDocsIndexPage layout, scoped to that single doc.
 */
import { useEffect, useState } from "react";
import { FileText, Loader2, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const FILES = [
  { filename: "MAIN_USER_FLOWS.md", label: "Main user flows (every screen)" },
  { filename: "USER_FLOWS.md", label: "Legacy user flows" },
  { filename: "SHELSEA_USER_FLOW.md", label: "Shelsea pilot flow" },
  { filename: "MAKEUP_HOST_PLAYBOOK.md", label: "Makeup host playbook" },
];

export default function UserflowsViewer() {
  const [active, setActive] = useState(FILES[0].filename);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let abort = false;
    const load = async () => {
      setLoading(true);
      setContent("");
      try {
        const r = await fetch(`${API}/admin/docs/${active}`, { headers: headers() });
        if (r.ok) {
          const d = await r.json();
          if (!abort) setContent(d.content || "");
        }
      } finally { if (!abort) setLoading(false); }
    };
    load();
    return () => { abort = true; };
  }, [active]);

  return (
    <div data-testid="userflows-viewer">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <FileText className="w-5 h-5" />
        <h1 className="text-xl font-bold">Userflows</h1>
        <Badge className="bg-purple-100 text-purple-700 border-0">{FILES.length} docs</Badge>
        <div className="flex-1" />
        <a
          href="/admin/docs"
          className="text-xs text-purple-600 hover:underline inline-flex items-center"
          data-testid="userflows-open-docs-index"
        >
          Open full docs index <ExternalLink className="w-3 h-3 ml-1" />
        </a>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Every screen, popup, analytics event, and tier gate captured in markdown. Source of truth lives at
        {" "}<code>/app/memory/MAIN_USER_FLOWS.md</code>.
      </p>

      <div className="flex gap-3">
        <div className="w-56 shrink-0 space-y-1">
          {FILES.map((f) => (
            <button
              key={f.filename}
              onClick={() => setActive(f.filename)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                active === f.filename ? "bg-white border shadow-sm font-medium" : "text-gray-600 hover:bg-white/60"
              }`}
              data-testid={`userflow-tab-${f.filename}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <Card className="flex-1">
          <CardContent className="p-4">
            {loading ? (
              <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
            ) : (
              <pre className="text-xs whitespace-pre-wrap font-mono text-gray-800" data-testid="userflows-content">
                {content || "No content"}
              </pre>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
